/**
 * v1.14.61.0 F5 — Wizard Garanzie pubblico (cliente).
 *
 * Componente che renderizza il wizard a 2 sotto-step:
 *   A) Contraente (ragione sociale, P.IVA, email, telefono, note)
 *   B) Per ogni ubicazione: valori contenuto + garanzie + opzionali
 * Autosave dopo ogni modifica (debounced 1.5s) via
 * /public/quoter/invites/{token}/garanzie-save-step.
 * Submit finale chiama /garanzie-submit e restituisce il riepilogo.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Save,
  Building2,
  ShieldCheck,
  Loader2,
  Sparkles,
  Check,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PALETTE = {
  navy: "#0B2545",
  navyDark: "#06182E",
  gold: "#C9A961",
  goldSoft: "#E2C988",
  cream: "#F8F4ED",
  ink: "#1A1A1A",
  text: "#3A3A3A",
  muted: "#6E6E6E",
  border: "#E5DFD4",
};

const numFmt = (n) =>
  Number.isFinite(Number(n)) ? Number(n).toLocaleString("it-IT") : "";

// ─────────────────────────────────────────────────────────────────────────
// Default garanzie state
// ─────────────────────────────────────────────────────────────────────────

const DEFAULT_GARANZIE = () => ({
  // INCENDIO
  incendio: {
    include: false,
    valore_fabbricato_eur: null,   // override anagrafica (editabile)
    valore_contenuto_eur: 0,
  },
  // FURTO — Primo Rischio Assoluto di default (a scelta del cliente)
  furto: {
    include: false,
    forma: "primo_rischio_assoluto",   // "primo_rischio_assoluto" | "valore_intero"
    valore_eur: 0,
    sub_cassaforte: { include: false, valore: 0 },
    sub_trasporto_valori: { include: false, valore: 0 },
    sub_archivi: { include: false, valore: 0 },
    sub_beni_aperto: { include: false, valore: 0 },
    sub_bestiame_aperto: { include: false, valore: 0 },     // solo agricolo
    sub_macchine_aperto: { include: false, valore: 0 },     // solo agricolo
  },
  // RC FABBRICATO §3.5
  rcf: {
    include: false,
    codice_rischio: "",            // 20111..20117
    massimale_eur: null,           // 500000 / 1000000 / 2500000 / 5000000
    rc_full: false,
  },
  // EVENTI CATASTROFALI / ATMOSFERICI
  vento_grandine: false,
  terremoto: false,
  inondazione: false,
  sovraccarico_neve: false,
  eventi_sociopolitici: false,
  terrorismo: false,
  // ESTENSIONI
  rottura_lastre: false,
  fenomeno_elettrico: false,
  beni_presso_terzi: false,
});

const DEFAULT_VALORI = () => ({
  valore_macchinario_eur: 0,
  valore_attrezzature_arredo_eur: 0,
  valore_elettronica_eur: 0,
  valore_merci_eur: 0,
});

// v1.14.61.0.2 — 7 codici rischio RCF §3.5 con descrizione human-readable.
// Tassi verbatim Sprint 3 P1 (pag 64 Mod 3063 INC).
const RCF_CODICI = [
  { code: "20111", label: "Condominio (≥¾ abitazioni/uffici/clinica)" },
  { code: "20112", label: "NON-condominio (≥¾ abitazioni/uffici/clinica)" },
  { code: "20113", label: "Musei/teatri/cinema/biblioteche" },
  { code: "20114", label: "Uso rurale" },
  { code: "20115", label: "Industriale/commerciale gestito dal proprietario (albergo, B&B, scuola, palestra)" },
  { code: "20116", label: "Industriale/commerciale dato in locazione a terzi" },
  { code: "20117", label: "Altro — richiede autorizzazione Reale Mutua (R.D.)" },
];

const RCF_MASSIMALI = [
  { value: 500000, label: "€ 500.000" },
  { value: 1000000, label: "€ 1.000.000" },
  { value: 2500000, label: "€ 2.500.000" },
  { value: 5000000, label: "€ 5.000.000" },
];

// Suggerisce il codice RCF in base al settore tariffa dall'anagrafica.
function suggestRcfCode(settore) {
  if (settore === "civile_agricolo") return "20111";
  if (settore === "altri_rischi_civili") return "20113";
  if (settore === "commercio_piccola_industria") return "20115";
  if (settore === "rischi_vari") return "20117";
  return "";
}

// ─────────────────────────────────────────────────────────────────────────
// Small UI atoms
// ─────────────────────────────────────────────────────────────────────────

const Field = ({ label, hint, children }) => (
  <label className="block text-sm">
    <div className="font-medium mb-1" style={{ color: PALETTE.navy }}>
      {label}
    </div>
    {children}
    {hint && (
      <div className="mt-1 text-[11px]" style={{ color: PALETTE.muted }}>
        {hint}
      </div>
    )}
  </label>
);

const TextInput = ({ value, onChange, ...rest }) => (
  <input
    type="text"
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    className="w-full px-3 py-2 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-offset-1"
    style={{
      borderColor: PALETTE.border,
      "--tw-ring-color": PALETTE.gold,
    }}
    {...rest}
  />
);

const NumberInput = ({ value, onChange, ...rest }) => (
  <input
    type="number"
    min={0}
    inputMode="decimal"
    value={value ?? 0}
    onChange={(e) => onChange(Number(e.target.value) || 0)}
    className="w-full px-3 py-2 rounded-lg border text-sm bg-white text-right tabular-nums focus:outline-none focus:ring-2"
    style={{
      borderColor: PALETTE.border,
      "--tw-ring-color": PALETTE.gold,
    }}
    {...rest}
  />
);

const SwitchRow = ({ label, hint, checked, onChange, testid }) => (
  <button
    type="button"
    data-testid={testid}
    role="switch"
    aria-checked={checked}
    data-checked={checked ? "true" : "false"}
    onClick={() => onChange(!checked)}
    className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-[#FFF6E8] transition w-full text-left"
    style={{
      borderColor: checked ? PALETTE.gold : PALETTE.border,
      background: checked ? "#FFF7E0" : "#fff",
    }}
  >
    <div
      className="w-9 h-5 rounded-full transition relative shrink-0 mt-1"
      style={{
        background: checked ? PALETTE.gold : "#CDCDCD",
      }}
    >
      <span
        className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all"
        style={{ left: checked ? 18 : 2 }}
      />
    </div>
    <div className="text-sm">
      <div className="font-medium" style={{ color: PALETTE.navy }}>
        {label}
      </div>
      {hint && (
        <div className="text-xs mt-0.5" style={{ color: PALETTE.muted }}>
          {hint}
        </div>
      )}
    </div>
  </button>
);

// ─────────────────────────────────────────────────────────────────────────
// Wizard Step A — Contraente
// ─────────────────────────────────────────────────────────────────────────

const ContraenteStep = ({ contraente, setContraente }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    <Field label="Ragione sociale / Nome cliente *">
      <TextInput
        value={contraente.ragione_sociale}
        onChange={(v) => setContraente({ ...contraente, ragione_sociale: v })}
        data-testid="contraente-ragione-sociale"
        placeholder="Es. Hotel Roma S.r.l."
      />
    </Field>
    <Field label="P.IVA / Codice Fiscale">
      <TextInput
        value={contraente.partita_iva}
        onChange={(v) => setContraente({ ...contraente, partita_iva: v })}
        data-testid="contraente-piva"
        placeholder="11 cifre P.IVA o 16 lettere CF"
      />
    </Field>
    <Field label="Email *">
      <TextInput
        value={contraente.email}
        onChange={(v) => setContraente({ ...contraente, email: v })}
        data-testid="contraente-email"
        placeholder="referente@azienda.it"
      />
    </Field>
    <Field label="Telefono">
      <TextInput
        value={contraente.telefono}
        onChange={(v) => setContraente({ ...contraente, telefono: v })}
        data-testid="contraente-telefono"
        placeholder="+39 ..."
      />
    </Field>
    <div className="sm:col-span-2">
      <Field
        label="Note operative (opzionali)"
        hint="Eventuali esigenze particolari, urgenze, ecc."
      >
        <textarea
          value={contraente.note ?? ""}
          onChange={(e) =>
            setContraente({ ...contraente, note: e.target.value })
          }
          rows={3}
          className="w-full px-3 py-2 rounded-lg border text-sm"
          style={{ borderColor: PALETTE.border }}
          data-testid="contraente-note"
        />
      </Field>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// Wizard Step B — Garanzie per ubicazione
// ─────────────────────────────────────────────────────────────────────────

const UbicazioneCard = ({ index, ana, sel, onChange }) => {
  const g = sel.garanzie || {};
  const isAgricolo = ana?.settore === "civile_agricolo";
  const isCpiOrRv =
    ana?.settore === "commercio_piccola_industria" ||
    ana?.settore === "rischi_vari";

  const totaleContenuto = useMemo(() => {
    const v = sel.valori_contenuto || {};
    return (
      Number(v.valore_macchinario_eur || 0) +
      Number(v.valore_attrezzature_arredo_eur || 0) +
      Number(v.valore_elettronica_eur || 0) +
      Number(v.valore_merci_eur || 0)
    );
  }, [sel.valori_contenuto]);

  const setVal = (k, v) =>
    onChange({
      ...sel,
      valori_contenuto: { ...sel.valori_contenuto, [k]: v },
    });
  const setGar = (k, v) =>
    onChange({ ...sel, garanzie: { ...g, [k]: v } });
  const setNested = (sec, patch) =>
    onChange({ ...sel, garanzie: { ...g, [sec]: { ...(g[sec] || {}), ...patch } } });
  const setNestedSub = (sec, subKey, patch) =>
    setNested(sec, {
      [subKey]: { ...((g[sec] || {})[subKey] || {}), ...patch },
    });

  // Inizializzazione lazy di alcuni default dipendenti dall'anagrafica
  useEffect(() => {
    // Pre-popola RCF.codice_rischio dal settore (solo se non settato)
    if (!g.rcf?.codice_rischio && ana?.settore) {
      const suggested = suggestRcfCode(ana.settore);
      if (suggested) setNested("rcf", { codice_rischio: suggested });
    }
    // Pre-popola incendio.valore_fabbricato_eur dall'anagrafica
    if (
      (g.incendio?.valore_fabbricato_eur === null ||
        g.incendio?.valore_fabbricato_eur === undefined) &&
      ana?.valore_fabbricato_eur
    ) {
      setNested("incendio", {
        valore_fabbricato_eur: Number(ana.valore_fabbricato_eur) || 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-testid={`wizard-ubicazione-${index}`}
      className="rounded-xl border overflow-hidden"
      style={{ borderColor: PALETTE.border, background: "#FFFFFF" }}
    >
      <div
        className="px-5 py-3 border-b flex items-center justify-between flex-wrap gap-2"
        style={{ borderColor: PALETTE.border, background: PALETTE.cream }}
      >
        <div>
          <div className="font-semibold" style={{ color: PALETTE.navy }}>
            Immobile {index + 1} — {ana.ubicazione_id}
          </div>
          <div className="text-xs" style={{ color: PALETTE.muted }}>
            {ana.indirizzo} — {ana.cap} {ana.comune} ({ana.provincia}) ·{" "}
            cl. {ana.classe_territoriale} · {ana.attivita_descrizione}
          </div>
        </div>
        <div className="text-xs" style={{ color: PALETTE.muted }}>
          Anagrafica fabbricato:{" "}
          <strong style={{ color: PALETTE.navy }}>
            € {numFmt(ana.valore_fabbricato_eur)}
          </strong>
        </div>
      </div>

      <div className="p-5 space-y-6">
        {/* ═══ INCENDIO ═══ */}
        <section
          data-testid={`section-incendio-${index}`}
          className="rounded-lg border p-4"
          style={{
            borderColor: g.incendio?.include ? PALETTE.gold : PALETTE.border,
            background: g.incendio?.include ? "#FFFCF5" : "#FFFFFF",
          }}
        >
          <SwitchRow
            testid={`gar-${index}-incendio`}
            label="🔥 Incendio (Fabbricato + Contenuto)"
            hint="Copre fuoco, fulmine, scoppio sul fabbricato e/o sul contenuto"
            checked={g.incendio?.include || false}
            onChange={(v) => setNested("incendio", { include: v })}
          />
          {g.incendio?.include && (
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <Field
                label="Valore Fabbricato per Incendio (€) *"
                hint="Valore di ricostruzione a nuovo da assicurare contro incendio. Pre-compilato dall'anagrafica, editabile."
              >
                <NumberInput
                  value={g.incendio?.valore_fabbricato_eur || 0}
                  onChange={(v) =>
                    setNested("incendio", { valore_fabbricato_eur: v })
                  }
                  data-testid={`incendio-${index}-vfabb`}
                />
              </Field>
              <Field
                label="Valore Contenuto per Incendio (€)"
                hint="Quanto vale il contenuto da assicurare (macchinari, attrezzature, merci, elettronica)?"
              >
                <NumberInput
                  value={g.incendio?.valore_contenuto_eur || 0}
                  onChange={(v) =>
                    setNested("incendio", { valore_contenuto_eur: v })
                  }
                  data-testid={`incendio-${index}-vcont`}
                />
              </Field>
            </div>
          )}
        </section>

        {/* ═══ FURTO ═══ */}
        <section
          data-testid={`section-furto-${index}`}
          className="rounded-lg border p-4"
          style={{
            borderColor: g.furto?.include ? PALETTE.gold : PALETTE.border,
            background: g.furto?.include ? "#FFFCF5" : "#FFFFFF",
          }}
        >
          <SwitchRow
            testid={`gar-${index}-furto`}
            label="🔒 Furto"
            hint="Copre furto/scippo/rapina del contenuto"
            checked={g.furto?.include || false}
            onChange={(v) => setNested("furto", { include: v })}
          />
          {g.furto?.include && (
            <div className="mt-4 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field
                  label="Forma assicurativa"
                  hint="«Primo Rischio Assoluto» = il cliente sceglie il valore (consigliato). «Valore Intero» = sull'intero contenuto."
                >
                  <select
                    value={g.furto?.forma || "primo_rischio_assoluto"}
                    onChange={(e) =>
                      setNested("furto", { forma: e.target.value })
                    }
                    className="w-full px-3 py-2 rounded-lg border text-sm bg-white"
                    style={{ borderColor: PALETTE.border }}
                    data-testid={`furto-${index}-forma`}
                  >
                    <option value="primo_rischio_assoluto">
                      Primo Rischio Assoluto (PRA)
                    </option>
                    <option value="valore_intero">Valore Intero</option>
                  </select>
                </Field>
                <Field
                  label={
                    g.furto?.forma === "valore_intero"
                      ? "Valore intero contenuto (€) *"
                      : "Valore da assicurare a PRA (€) *"
                  }
                  hint="Quanto vale il contenuto che vuoi tutelare in caso di furto? (es. macchine, scorte, attrezzature)"
                >
                  <NumberInput
                    value={g.furto?.valore_eur || 0}
                    onChange={(v) => setNested("furto", { valore_eur: v })}
                    data-testid={`furto-${index}-valore`}
                  />
                </Field>
              </div>

              {/* Sub-coperture furto */}
              <details
                className="rounded-md border"
                style={{ borderColor: PALETTE.border }}
              >
                <summary
                  className="px-3 py-2 cursor-pointer text-xs font-semibold uppercase tracking-wider"
                  style={{ color: PALETTE.navy, background: PALETTE.cream }}
                >
                  Sub-coperture furto (opzionali)
                </summary>
                <div className="p-3 space-y-2">
                  <SubCopRow
                    label="Valori in cassaforte"
                    testid={`furto-${index}-cassaforte`}
                    state={g.furto?.sub_cassaforte}
                    onChange={(patch) =>
                      setNestedSub("furto", "sub_cassaforte", patch)
                    }
                  />
                  <SubCopRow
                    label="Trasporto valori"
                    testid={`furto-${index}-trasporto`}
                    state={g.furto?.sub_trasporto_valori}
                    onChange={(patch) =>
                      setNestedSub("furto", "sub_trasporto_valori", patch)
                    }
                  />
                  <SubCopRow
                    label="Archivi e documenti"
                    testid={`furto-${index}-archivi`}
                    state={g.furto?.sub_archivi}
                    onChange={(patch) =>
                      setNestedSub("furto", "sub_archivi", patch)
                    }
                  />
                  {isCpiOrRv && (
                    <SubCopRow
                      label="Beni all'aperto (CPI/RV)"
                      testid={`furto-${index}-beni-aperto`}
                      state={g.furto?.sub_beni_aperto}
                      onChange={(patch) =>
                        setNestedSub("furto", "sub_beni_aperto", patch)
                      }
                    />
                  )}
                  {isAgricolo && (
                    <>
                      <SubCopRow
                        label="Furto bestiame all'aperto"
                        testid={`furto-${index}-bestiame`}
                        state={g.furto?.sub_bestiame_aperto}
                        onChange={(patch) =>
                          setNestedSub("furto", "sub_bestiame_aperto", patch)
                        }
                      />
                      <SubCopRow
                        label="Furto macchine agricole all'aperto"
                        testid={`furto-${index}-macchine`}
                        state={g.furto?.sub_macchine_aperto}
                        onChange={(patch) =>
                          setNestedSub("furto", "sub_macchine_aperto", patch)
                        }
                      />
                    </>
                  )}
                </div>
              </details>
            </div>
          )}
        </section>

        {/* ═══ RC FABBRICATO §3.5 ═══ */}
        <section
          data-testid={`section-rcf-${index}`}
          className="rounded-lg border p-4"
          style={{
            borderColor: g.rcf?.include ? PALETTE.gold : PALETTE.border,
            background: g.rcf?.include ? "#FFFCF5" : "#FFFFFF",
          }}
        >
          <SwitchRow
            testid={`gar-${index}-rc`}
            label="🛡️ Responsabilità Civile del Fabbricato (§3.5)"
            hint="Copre i danni a terzi causati dal fabbricato (caduta cornicioni, rottura tubi, ecc.)"
            checked={g.rcf?.include || false}
            onChange={(v) => setNested("rcf", { include: v })}
          />
          {g.rcf?.include && (
            <div className="mt-4 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field
                  label="Codice Rischio §3.5 *"
                  hint="Tipo di immobile secondo la tariffa Reale Mutua. Es. 20115 = capannone/albergo gestito dal proprietario"
                >
                  <select
                    value={g.rcf?.codice_rischio || ""}
                    onChange={(e) =>
                      setNested("rcf", { codice_rischio: e.target.value })
                    }
                    className="w-full px-3 py-2 rounded-lg border text-sm bg-white"
                    style={{ borderColor: PALETTE.border }}
                    data-testid={`rcf-${index}-codice`}
                  >
                    <option value="">— Seleziona codice —</option>
                    {RCF_CODICI.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code} — {c.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  label="Massimale (€) *"
                  hint="Il limite massimo che la polizza pagherà per danni a terzi"
                >
                  <select
                    value={g.rcf?.massimale_eur ?? ""}
                    onChange={(e) =>
                      setNested("rcf", {
                        massimale_eur: e.target.value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                    className="w-full px-3 py-2 rounded-lg border text-sm bg-white"
                    style={{ borderColor: PALETTE.border }}
                    data-testid={`rcf-${index}-massimale`}
                  >
                    <option value="">— Seleziona fascia —</option>
                    {RCF_MASSIMALI.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="pt-2">
                <SwitchRow
                  testid={`rcf-${index}-full`}
                  label="Add-on RC FULL"
                  hint="RCO (verso dipendenti) + interruzioni + inquinamento accidentale. +10% Civile/Agr/ARC oppure +20% CPI/RV"
                  checked={g.rcf?.rc_full || false}
                  onChange={(v) => setNested("rcf", { rc_full: v })}
                />
              </div>
              {g.rcf?.codice_rischio === "20117" && (
                <div
                  className="rounded-md p-3 text-xs"
                  style={{
                    background: "#FFF7E0",
                    color: "#7A4F00",
                    border: "1px solid #E2B97A",
                  }}
                  data-testid={`rcf-${index}-rd-warning`}
                >
                  ⚠ Codice 20117 «Fabbricato diverso dai precedenti» = Rischio
                  Direzionale. Il tasso verrà negoziato manualmente con
                  Reale Mutua: il preventivo finale richiede l&apos;autorizzazione
                  della direzione tecnica.
                </div>
              )}
            </div>
          )}
        </section>

        {/* ═══ Eventi catastrofali ═══ */}
        <section>
          <div
            className="text-xs font-semibold uppercase tracking-wider mb-3"
            style={{ color: PALETTE.muted }}
          >
            ⚡ Eventi catastrofali / atmosferici
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <SwitchRow
              testid={`gar-${index}-vg`}
              label="Vento e Grandine"
              checked={g.vento_grandine}
              onChange={(v) => setGar("vento_grandine", v)}
            />
            <SwitchRow
              testid={`gar-${index}-terr`}
              label="Terremoto"
              hint="Richiede autorizzazione direzionale"
              checked={g.terremoto}
              onChange={(v) => setGar("terremoto", v)}
            />
            <SwitchRow
              label="Inondazione / Alluvione"
              hint="Richiede autorizzazione direzionale"
              checked={g.inondazione}
              onChange={(v) => setGar("inondazione", v)}
            />
            <SwitchRow
              label="Sovraccarico Neve"
              hint="Richiede V&G attivo"
              checked={g.sovraccarico_neve}
              onChange={(v) => setGar("sovraccarico_neve", v)}
            />
            <SwitchRow
              label="Eventi Socio-politici"
              checked={g.eventi_sociopolitici}
              onChange={(v) => setGar("eventi_sociopolitici", v)}
            />
            <SwitchRow
              label="Terrorismo"
              checked={g.terrorismo}
              onChange={(v) => setGar("terrorismo", v)}
            />
          </div>
        </section>

        {/* ═══ Estensioni ═══ */}
        <section>
          <div
            className="text-xs font-semibold uppercase tracking-wider mb-3"
            style={{ color: PALETTE.muted }}
          >
            ➕ Estensioni di copertura
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <SwitchRow
              label="Rottura Lastre"
              checked={g.rottura_lastre}
              onChange={(v) => setGar("rottura_lastre", v)}
            />
            <SwitchRow
              label="Fenomeno Elettrico"
              checked={g.fenomeno_elettrico}
              onChange={(v) => setGar("fenomeno_elettrico", v)}
            />
            <SwitchRow
              label="Beni presso terzi"
              hint="+20% premio contenuto"
              checked={g.beni_presso_terzi}
              onChange={(v) => setGar("beni_presso_terzi", v)}
            />
          </div>
        </section>

        {/* ═══ Recap valori contenuto (info, non più input principale) ═══ */}
        <details
          className="rounded-md border"
          style={{ borderColor: PALETTE.border }}
          data-testid={`valori-contenuto-${index}`}
        >
          <summary
            className="px-3 py-2 cursor-pointer text-xs font-semibold uppercase tracking-wider"
            style={{ color: PALETTE.navy, background: PALETTE.cream }}
          >
            Dettaglio composizione contenuto (opzionale)
          </summary>
          <div className="p-3">
            <p className="text-xs mb-3" style={{ color: PALETTE.muted }}>
              I valori qui sotto sono solo informativi: il premio Incendio usa
              i valori inseriti nella sezione 🔥 Incendio sopra; il premio
              Furto usa il valore PRA inserito nella sezione 🔒 Furto.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Field label="Macchinario">
                <NumberInput
                  value={sel.valori_contenuto.valore_macchinario_eur}
                  onChange={(v) => setVal("valore_macchinario_eur", v)}
                />
              </Field>
              <Field label="Attrezzature/Arredo">
                <NumberInput
                  value={sel.valori_contenuto.valore_attrezzature_arredo_eur}
                  onChange={(v) =>
                    setVal("valore_attrezzature_arredo_eur", v)
                  }
                />
              </Field>
              <Field label="Elettronica">
                <NumberInput
                  value={sel.valori_contenuto.valore_elettronica_eur}
                  onChange={(v) => setVal("valore_elettronica_eur", v)}
                />
              </Field>
              <Field label="Merci">
                <NumberInput
                  value={sel.valori_contenuto.valore_merci_eur}
                  onChange={(v) => setVal("valore_merci_eur", v)}
                />
              </Field>
            </div>
            <div
              className="mt-2 text-xs px-2 py-1 rounded inline-block"
              style={{ background: PALETTE.cream, color: PALETTE.navy }}
            >
              Totale composizione: € {numFmt(totaleContenuto)}
            </div>
          </div>
        </details>
      </div>
    </div>
  );
};

// Componente riga sub-copertura furto: switch + valore inline
const SubCopRow = ({ label, testid, state, onChange }) => {
  const enabled = !!state?.include;
  return (
    <div
      className="flex items-center gap-3 flex-wrap"
      data-testid={testid}
    >
      <label className="flex items-center gap-2 cursor-pointer text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onChange({ include: e.target.checked })}
          className="w-4 h-4"
        />
        <span style={{ color: PALETTE.navy }}>{label}</span>
      </label>
      {enabled && (
        <div className="flex-1 max-w-[220px]">
          <input
            type="number"
            min={0}
            value={state?.valore || 0}
            onChange={(e) =>
              onChange({ valore: Number(e.target.value) || 0 })
            }
            placeholder="Valore €"
            className="w-full px-2 py-1 rounded border text-sm text-right tabular-nums"
            style={{ borderColor: PALETTE.border }}
            data-testid={`${testid}-valore`}
          />
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// MAIN WIZARD
// ─────────────────────────────────────────────────────────────────────────

export default function GaranzieWizard({ token, onSubmitted, onBack }) {
  const [state, setState] = useState(null);
  const [contraente, setContraente] = useState({
    ragione_sociale: "",
    partita_iva: "",
    email: "",
    telefono: "",
    note: "",
  });
  const [selections, setSelections] = useState([]);
  const [substep, setSubstep] = useState(0); // 0 = contraente, 1..N = ubicazioni, last = riepilogo
  const [submitting, setSubmitting] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const debounceRef = useRef(null);

  // Carica stato iniziale
  useEffect(() => {
    axios
      .get(`${API}/public/quoter/invites/${token}/garanzie-state`)
      .then(({ data }) => {
        setState(data);
        // Init selections: ANA preview ← state, ognuna con default + eventuale state pregresso
        const restored = data.step2_data?.ubicazioni || [];
        const restoredById = Object.fromEntries(
          restored.map((u) => [u.ubicazione_id, u]),
        );
        setSelections(
          (data.anagrafica || []).map((a) => {
            const r = restoredById[a.ubicazione_id] || {};
            return {
              ubicazione_id: a.ubicazione_id,
              valori_contenuto: {
                ...DEFAULT_VALORI(),
                ...(r.valori_contenuto || {}),
              },
              garanzie: { ...DEFAULT_GARANZIE(), ...(r.garanzie || {}) },
            };
          }),
        );
        const rc = data.step2_data?.contraente;
        if (rc) setContraente((c) => ({ ...c, ...rc }));
      })
      .catch((e) => {
        toast.error("Errore caricamento wizard");
        console.error(e);
      });
  }, [token]);

  // Autosave debounced (1.5s) on contraente or selections change
  useEffect(() => {
    if (!state) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        await axios.post(
          `${API}/public/quoter/invites/${token}/garanzie-save-step`,
          {
            contraente,
            ubicazioni: selections,
            step_index: substep,
          },
        );
        setLastSavedAt(new Date());
      } catch (e) {
        // silente — l'utente non si accorge ma vede l'indicatore
      }
    }, 1500);
    return () => clearTimeout(debounceRef.current);
  }, [contraente, selections, substep, token, state]);

  const ubCount = selections.length;
  const totalSteps = ubCount + 2; // contraente + N ubicazioni + riepilogo
  const isContraenteStep = substep === 0;
  const isUbicazioneStep = substep > 0 && substep <= ubCount;
  const isRiepilogoStep = substep === totalSteps - 1;
  const currentUbIndex = isUbicazioneStep ? substep - 1 : -1;

  const canProceed = useMemo(() => {
    if (isContraenteStep) {
      return (
        (contraente.ragione_sociale || "").trim().length >= 2 &&
        /\S+@\S+\.\S+/.test(contraente.email || "")
      );
    }
    if (isUbicazioneStep) {
      const sel = selections[currentUbIndex];
      const g = sel?.garanzie || {};
      // Almeno una garanzia base attiva
      const someBase =
        g.incendio?.include || g.furto?.include || g.rcf?.include;
      if (!someBase) return false;
      // Incendio: serve almeno valore fabbricato O contenuto > 0
      if (g.incendio?.include) {
        const vf = Number(g.incendio?.valore_fabbricato_eur || 0);
        const vc = Number(g.incendio?.valore_contenuto_eur || 0);
        if (vf <= 0 && vc <= 0) return false;
      }
      // Furto: valore_eur > 0 obbligatorio
      if (g.furto?.include) {
        if (!Number(g.furto?.valore_eur || 0)) return false;
      }
      // RCF: codice + massimale obbligatori
      if (g.rcf?.include) {
        if (!g.rcf?.codice_rischio) return false;
        if (!g.rcf?.massimale_eur) return false;
      }
      return true;
    }
    return true;
  }, [
    isContraenteStep,
    isUbicazioneStep,
    contraente,
    selections,
    currentUbIndex,
  ]);

  const goNext = () => {
    if (!canProceed) {
      toast.error("Compila i campi obbligatori prima di proseguire");
      return;
    }
    setSubstep(Math.min(substep + 1, totalSteps - 1));
  };

  const goBack = () => {
    if (substep === 0) {
      onBack?.();
    } else {
      setSubstep(substep - 1);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/public/quoter/invites/${token}/garanzie-submit`,
        { contraente, ubicazioni: selections },
      );
      toast.success("Preventivo emesso correttamente");
      onSubmitted?.(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(
        typeof detail === "object" ? detail.message : detail || "Errore",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!state) {
    return (
      <div className="flex items-center gap-2 p-8">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span>Caricamento wizard…</span>
      </div>
    );
  }

  const rcBands = state.tariff_meta?.rc_fabbricato_bands || [];

  return (
    <div
      data-testid="garanzie-wizard"
      className="rounded-2xl border shadow-sm overflow-hidden"
      style={{ borderColor: PALETTE.border, background: "#FFFFFF" }}
    >
      <div
        className="px-6 py-4 border-b flex items-center justify-between flex-wrap gap-3"
        style={{ borderColor: PALETTE.border, background: PALETTE.cream }}
      >
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-5 h-5" style={{ color: PALETTE.navy }} />
          <div>
            <h2
              className="font-serif text-xl"
              style={{
                color: PALETTE.navy,
                fontFamily: "Georgia, serif",
              }}
            >
              Step 2 — Scelta garanzie
            </h2>
            <p className="text-xs" style={{ color: PALETTE.muted }}>
              {isContraenteStep && "Dati del contraente"}
              {isUbicazioneStep &&
                `Immobile ${currentUbIndex + 1} di ${ubCount}`}
              {isRiepilogoStep && "Riepilogo prima dell'emissione"}
            </p>
          </div>
        </div>
        <div className="text-[11px] flex items-center gap-1.5" style={{ color: PALETTE.muted }}>
          {lastSavedAt ? (
            <>
              <Check className="w-3 h-3" style={{ color: "#1B8E5C" }} />
              Salvato {lastSavedAt.toLocaleTimeString("it-IT")}
            </>
          ) : (
            <span>Compila e procedi — autosave attivo</span>
          )}
        </div>
      </div>

      <div className="p-6">
        {isContraenteStep && (
          <ContraenteStep
            contraente={contraente}
            setContraente={setContraente}
          />
        )}
        {isUbicazioneStep && (
          <UbicazioneCard
            index={currentUbIndex}
            ana={state.anagrafica[currentUbIndex]}
            sel={selections[currentUbIndex]}
            rcBands={rcBands}
            onChange={(s) => {
              const arr = [...selections];
              arr[currentUbIndex] = s;
              setSelections(arr);
            }}
          />
        )}
        {isRiepilogoStep && (
          <RiepilogoStep
            contraente={contraente}
            selections={selections}
            anagrafica={state.anagrafica}
          />
        )}
      </div>

      <div
        className="px-6 py-4 border-t flex items-center justify-between"
        style={{ borderColor: PALETTE.border }}
      >
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 text-sm"
          style={{ color: PALETTE.muted }}
          data-testid="wizard-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Indietro
        </button>

        {!isRiepilogoStep && (
          <button
            onClick={goNext}
            disabled={!canProceed}
            className="px-5 py-2.5 rounded-full inline-flex items-center gap-2 font-medium text-sm transition disabled:opacity-50"
            style={{ background: PALETTE.navy, color: "#FFFFFF" }}
            data-testid="wizard-next-btn"
          >
            Prosegui
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
        {isRiepilogoStep && (
          <button
            onClick={submit}
            disabled={submitting}
            className="px-5 py-2.5 rounded-full inline-flex items-center gap-2 font-semibold text-sm transition disabled:opacity-60"
            style={{ background: PALETTE.gold, color: PALETTE.navy }}
            data-testid="wizard-submit-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            Emetti il preventivo
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Sotto-step Riepilogo
// ─────────────────────────────────────────────────────────────────────────

function RiepilogoStep({ contraente, selections, anagrafica }) {
  const anagraficaById = Object.fromEntries(
    anagrafica.map((a) => [a.ubicazione_id, a]),
  );
  return (
    <div className="space-y-5">
      <section>
        <div
          className="text-xs font-semibold uppercase tracking-wider mb-2"
          style={{ color: PALETTE.muted }}
        >
          Contraente
        </div>
        <div
          className="rounded-lg border p-4 text-sm"
          style={{ borderColor: PALETTE.border, background: PALETTE.cream }}
        >
          <div className="font-semibold" style={{ color: PALETTE.navy }}>
            {contraente.ragione_sociale || "—"}
          </div>
          <div style={{ color: PALETTE.text }}>
            P.IVA/CF: {contraente.partita_iva || "—"} · {contraente.email || "—"}
            {contraente.telefono && ` · ${contraente.telefono}`}
          </div>
          {contraente.note && (
            <div
              className="mt-2 text-xs"
              style={{ color: PALETTE.muted }}
            >
              Note: {contraente.note}
            </div>
          )}
        </div>
      </section>

      <section>
        <div
          className="text-xs font-semibold uppercase tracking-wider mb-2"
          style={{ color: PALETTE.muted }}
        >
          Immobili e garanzie selezionate
        </div>
        <div className="grid gap-3">
          {selections.map((s, i) => {
            const ana = anagraficaById[s.ubicazione_id] || {};
            const g = s.garanzie || {};
            const activeBadges = [];
            if (g.incendio?.include) {
              const vf = Number(g.incendio?.valore_fabbricato_eur || 0);
              const vc = Number(g.incendio?.valore_contenuto_eur || 0);
              activeBadges.push({
                key: "incendio",
                txt: `🔥 Incendio · F.€${numFmt(vf)} · C.€${numFmt(vc)}`,
              });
            }
            if (g.furto?.include) {
              const forma = g.furto?.forma === "valore_intero" ? "VI" : "PRA";
              activeBadges.push({
                key: "furto",
                txt: `🔒 Furto ${forma} · €${numFmt(g.furto?.valore_eur || 0)}`,
              });
            }
            if (g.rcf?.include) {
              const mas = g.rcf?.massimale_eur;
              activeBadges.push({
                key: "rcf",
                txt: `🛡️ RCF ${g.rcf?.codice_rischio} · max €${numFmt(mas)}${g.rcf?.rc_full ? " · FULL" : ""}`,
              });
            }
            for (const k of [
              "vento_grandine", "terremoto", "inondazione",
              "sovraccarico_neve", "eventi_sociopolitici", "terrorismo",
              "rottura_lastre", "fenomeno_elettrico", "beni_presso_terzi",
            ]) {
              if (g[k]) activeBadges.push({
                key: k, txt: k.replace(/_/g, " "),
              });
            }
            return (
              <div
                key={s.ubicazione_id}
                className="rounded-lg border p-4 text-sm"
                style={{ borderColor: PALETTE.border }}
                data-testid={`riepilogo-ubicazione-${i}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div
                      className="font-semibold"
                      style={{ color: PALETTE.navy }}
                    >
                      <Building2 className="inline w-4 h-4 mr-1" />
                      {ana.indirizzo}, {ana.comune} ({ana.provincia})
                    </div>
                    <div className="text-xs" style={{ color: PALETTE.muted }}>
                      {ana.attivita_descrizione} · cl.{" "}
                      {ana.classe_territoriale}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {activeBadges.map((b) => (
                    <span
                      key={b.key}
                      className="text-[11px] px-2 py-0.5 rounded-full"
                      style={{
                        background: PALETTE.cream,
                        color: PALETTE.navy,
                      }}
                    >
                      {b.txt}
                    </span>
                  ))}
                  {activeBadges.length === 0 && (
                    <span className="text-[11px]" style={{ color: PALETTE.muted }}>
                      Nessuna garanzia attivata
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <div
        className="rounded-lg p-4 text-xs flex items-start gap-2"
        style={{ background: PALETTE.cream, color: PALETTE.text }}
      >
        <Save
          className="w-4 h-4 shrink-0 mt-0.5"
          style={{ color: PALETTE.navy }}
        />
        <div>
          Premendo <strong>«Emetti il preventivo»</strong> il sistema
          calcolerà i premi finali secondo la tariffa Reale Mutua e ti
          invierà il PDF brandizzato + Excel riepilogo + Fascicolo
          Informativo. Il preventivo resta una proposta e non vincola le
          parti.
        </div>
      </div>
    </div>
  );
}
